import React, { useState, useEffect } from "react";
import { formatPrice, getIntervalName } from "../utils/pricing";
import type { SubscriptionPlan } from "../services/accountService";

/**
 * Trial eligibility information
 */
export interface TrialInfo {
  eligible: boolean;
  trialDays: number;
  reason: string;
}

interface PlanSelectorProps {
  plans: SubscriptionPlan[];
  trialInfo?: TrialInfo | null;
  onSelect: (planId: string, stripePriceId: string) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Custom button label. Defaults to "Wybierz plan" / "Przekierowywanie..." when loading */
  buttonLabel?: string;
  /** Custom button label shown during loading. Defaults to "Przekierowywanie..." */
  loadingLabel?: string;
  /** Payment source for conditional messaging */
  paymentSource?: string;
  /** Whether the current subscription is in trial period. Controls trial-specific messaging. */
  isTrialing?: boolean;
}

/**
 * PlanSelector Component
 *
 * Reusable component for selecting subscription plans and prices.
 * Used in both registration and subscription activation flows.
 *
 * Features:
 * - Trial banner when eligible
 * - Plan cards with price interval selection
 * - Auto-selects first plan and price
 * - Visual feedback for selection
 * - VAT disclosure
 */
export const PlanSelector: React.FC<PlanSelectorProps> = ({
  plans,
  trialInfo,
  onSelect,
  loading = false,
  disabled = false,
  buttonLabel = "Wybierz plan",
  loadingLabel = "Przekierowywanie...",
  paymentSource,
  isTrialing = false,
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

  // Auto-select first plan and price on mount
  useEffect(() => {
    if (plans.length > 0 && !selectedPlanId) {
      const firstPlan = plans[0];
      setSelectedPlanId(firstPlan.id);

      // Auto-select first price (usually monthly)
      if (firstPlan.prices && firstPlan.prices.length > 0) {
        setSelectedPriceId(firstPlan.prices[0].stripePriceId);
      }
    }
  }, [plans, selectedPlanId]);

  const handlePriceSelect = (planId: string, priceId: string) => {
    setSelectedPlanId(planId);
    setSelectedPriceId(priceId);
  };

  const handleSelectClick = () => {
    if (selectedPlanId && selectedPriceId) {
      onSelect(selectedPlanId, selectedPriceId);
    }
  };

  if (plans.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Brak dostępnych planów subskrypcji.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Trial Banner */}
      {trialInfo?.eligible && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-green-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                🎉 Okres próbny {trialInfo.trialDays} dni
              </h3>
              <p className="mt-1 text-sm text-green-700">
                Możesz korzystać z pełnych funkcji systemu przez{" "}
                {trialInfo.trialDays} dni. Po tym czasie automatycznie
                pobierzemy pierwszą płatność.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Plan Cards */}
      <div className="space-y-4">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition"
          >
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-gray-900">
                {plan.name}
              </h3>
              {plan.description && (
                <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
              )}
            </div>

            {/* Price Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {plan.prices?.map((price) => (
                <label
                  key={price.id}
                  className={`cursor-pointer border-2 rounded-lg p-4 transition ${
                    selectedPriceId === price.stripePriceId
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <input
                    type="radio"
                    name="stripePriceId"
                    value={price.stripePriceId}
                    checked={selectedPriceId === price.stripePriceId}
                    onChange={() =>
                      !disabled &&
                      handlePriceSelect(plan.id, price.stripePriceId)
                    }
                    disabled={disabled}
                    className="sr-only"
                  />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {formatPrice(price.amount, price.currency)}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      {price.billingIntervalLabel || getIntervalName(
                        price.billingInterval === "yearly" ? "year" : "month",
                        price.intervalCount
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Select Button */}
      <button
        onClick={handleSelectClick}
        disabled={!selectedPlanId || !selectedPriceId || loading || disabled}
        className="w-full md:w-auto px-6 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
      >
        {loading ? loadingLabel : buttonLabel}
      </button>

      {/* VAT Disclosure */}
      <p className="text-sm text-gray-500 text-center">
        Ceny netto. Do każdej ceny zostanie doliczony VAT 23%.
      </p>

      {/* AC-E7: Conditional payment info message per paymentSource — only shown during trial */}
      {isTrialing && paymentSource === "bank_transfer" ? (
        <p className="text-sm text-primary-800 text-center bg-primary-50 border border-primary-200 rounded-lg p-3">
          7 dni przed końcem trialu wyślemy proformę do opłacenia przelewem.
        </p>
      ) : isTrialing && paymentSource != null ? (
        <p className="text-sm text-gray-500 text-center">
          Po zakończeniu okresu próbnego automatycznie pobierzemy pierwszą płatność.
        </p>
      ) : null}
    </div>
  );
};
